import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ListChecks, X } from "lucide-react";
import { ReimbursementStatusBarExpandedContext } from "./ReimbursementStatusBarContext";

interface ReimbursementFormProgressDrawerProps {
  statusBar: ReactNode;
}

export function ReimbursementFormProgressDrawer({
  statusBar,
}: ReimbursementFormProgressDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;

    closeRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        window.requestAnimationFrame(() => triggerRef.current?.focus());
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const closeDrawer = () => {
    setIsOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  return (
    <>
      {!isOpen ? (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label="Open requirements checklist"
          aria-controls="reimbursement-form-progress-drawer"
          aria-expanded="false"
          data-testid="reimbursement-form-progress-trigger"
          className="fixed right-0 top-1/2 z-[999] flex h-11 w-10 -translate-y-1/2 items-center justify-center rounded-l-xl bg-[#1597ff] text-white opacity-70 shadow-lg transition hover:bg-[#0d7acc] hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1597ff] focus-visible:ring-offset-2 lg:hidden"
        >
          <ListChecks className="h-5 w-5" aria-hidden />
        </button>
      ) : (
        <aside
          id="reimbursement-form-progress-drawer"
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          data-testid="reimbursement-form-progress-drawer"
          className="fixed right-0 top-1/2 z-[10000] flex h-auto max-h-[85dvh] w-[min(90vw,26rem)] -translate-y-1/2 flex-col overflow-hidden rounded-l-xl border-l border-slate-200 bg-slate-50 shadow-[-8px_0_32px_rgba(15,23,42,0.16)] lg:hidden"
        >
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3.5">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-[#1597ff]">
                <ListChecks className="h-5 w-5" aria-hidden />
              </span>
              <h2 id={titleId} className="text-base font-bold text-slate-900">
                Requirements Checklist
              </h2>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={closeDrawer}
              aria-label="Close requirements checklist"
              data-testid="reimbursement-form-progress-close"
              className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1597ff]"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto overscroll-contain p-4">
            <ReimbursementStatusBarExpandedContext.Provider value>
              {statusBar}
            </ReimbursementStatusBarExpandedContext.Provider>
          </div>
        </aside>
      )}
    </>
  );
}
